<script setup lang="ts">
import { Ref, computed, inject, provide, ref, watch } from "vue";
import MButton from "../MButton";
import MExpander from "../MExpander";
import MLayoutStack from "../MLayoutStack";
import MBadge from "../MBadge";
import MSeparator from "../MSeparator";
import { setInGroup } from "../../../composables/horizontalLabel";
import { isObject } from "vue-composable";
import { useTenant } from "../../../store";
import { useApp } from "../../../store";
import { usePatientBanner } from "../../../store";

import { formatAddressToLink } from "../../../utils/address";
import { formatDate, getAge } from "../../../utils/date";
import { PropsOf } from "../../../utils/types";
import MTelephoneNumber from "../MTelephoneNumber";
import axios from "../../../utils/axios";
import MLink from "../MLink";
import MDescriptionList, { MDescriptionListItem } from "../MDescriptionList";
import { Patient } from "./types";

const props = defineProps<{
  secondary?: boolean;
  patient?: Patient;
  patientId?: string;
  // Client-side routing only, for a patientId that belongs to another tenant (e.g. a
  // delegate working agreement booking) -- sets the X-Delegated-Tenant-ID header and
  // swaps baseURL for this fetch (see utils/axios.ts). Never part of any payload.
  tenantId?: string | null;
  headingLevel?: "h1" | "h2" | "h3";
  badges?: PropsOf<typeof MBadge>[];
  lightBackground?: boolean;
  blueAccent?: boolean;
  noBorder?: boolean;
  small?: boolean;
  banner?: boolean;
  disableLink?: boolean;
  open?: boolean;
  boxShadow?: boolean;
}>();

const tenant = useTenant();
const { setHeaderHeight, updatePatientTitle } = useApp();

setInGroup(ref(true));
provide("labelHorizontal", false);

const store = usePatientBanner();
const patientBanner = inject<Ref<Patient> | undefined>("patientBanner", undefined);

// The shared store only ever fetches the local tenant, so an explicit tenantId (e.g. a
// delegate working agreement booking, see props.tenantId doc comment) bypasses it entirely
// in favour of a direct cross-tenant fetch below.
const entry = computed(() =>
  props.patientId && !props.tenantId ? store.entries.get(props.patientId) : undefined,
);
const fetchedPatient = ref<Patient | null>(null);
const fetchError = ref(false);

// ENG-21579 patientId path reads the shared store; props.patient / injected patientBanner unchanged.
const localPatient = computed(
  () => props.patient ?? patientBanner?.value ?? entry.value?.data ?? fetchedPatient.value,
);
const loading = computed(() => (props.tenantId ? false : entry.value?.loading ?? false));
const error = computed(() => (props.tenantId ? fetchError.value : entry.value?.error ?? false));

// A stub Patient (find-create-or-refresh-external-patient) only carries identity/
// contact fields -- risk alerts, safeguarding flags etc. never sync over. Once the
// locally-resolved patient reveals isExternalPatient, redirectedPatient holds the
// origin tenant's own banner data instead, which wins over the local (incomplete)
// one in the computed below.
const redirectedPatient = ref<Patient | null>(null);
let redirecting = false;

const patient = computed(() => redirectedPatient.value ?? localPatient.value);

async function redirectToOriginIfExternal(candidate: Patient | null) {
  if (
    !candidate?.isExternalPatient ||
    !candidate.externalPatientOriginTenantId ||
    !candidate.externalPatientOriginPatientId
  ) {
    redirectedPatient.value = null;
    return;
  }
  if (redirecting) return;
  redirecting = true;
  try {
    const { data } = await axios.get(
      `/patient/data/patient/patient-banner/${candidate.externalPatientOriginPatientId}`,
      { tenantId: candidate.externalPatientOriginTenantId },
    );
    redirectedPatient.value = data;
    updatePatientTitle(data?.displayName);
  } catch {
    // Leave redirectedPatient unset -- fall back to the (incomplete) local stub
    // data rather than losing the banner entirely.
  } finally {
    redirecting = false;
  }
}

watch(localPatient, redirectToOriginIfExternal, { immediate: true });

// ENG-21579 derive from patient (not a null→data watcher) so shared/second mounts get badges too.
const badges = computed<PropsOf<typeof MBadge>[] | null>(() => {
  const p = patient.value;
  return isObject(p) && "badges" in p && Array.isArray(p.badges) ? p.badges : null;
});

const extras = computed(() =>
  patient.value?.extras
    ? patient.value.extras.map((item) => {
        return {
          ...item,
          link: item.type === "address" ? formatAddressToLink(item.value) : item?.link,
        };
      })
    : [],
);

const showInfo = ref(props.open);

function hasValue(v: string) {
  return v !== "None recorded";
}

// Explicit cross-tenant fetch only -- the local-tenant case is handled by the shared store.
async function retrievePatient() {
  if (!props.patientId || !props.tenantId) return;

  await axios
    .get(`/patient/data/patient/patient-banner/${props.patientId}`, {
      tenantId: props.tenantId,
    })
    .then((res) => {
      fetchedPatient.value = res.data;
      if (fetchedPatient.value?.displayName) {
        updatePatientTitle(fetchedPatient.value.displayName);
      }
    })
    .catch(() => (fetchError.value = true));
}

watch(
  () => showInfo.value,
  () => {
    setTimeout(() => setHeaderHeight(), 510);
  },
);

watch([() => props.patientId, () => props.tenantId], retrievePatient, { immediate: true });

// ENG-21579 ref-counted acquire/release of the shared store entry. ENG-20644 skip when no
// patientId; also skipped for an explicit cross-tenant fetch (retrievePatient above handles it).
watch(
  () => (props.tenantId ? undefined : props.patientId),
  (id, _prev, onCleanup) => {
    if (!id) return;
    store.acquire(id);
    onCleanup(() => store.release(id));
  },
  { immediate: true },
);

watch(
  () => patient.value,
  (newPatient) => {
    if (props.patientId && newPatient?.displayName) updatePatientTitle(newPatient.displayName);
    setTimeout(() => setHeaderHeight(), 100);
  },
  { immediate: true },
);
// ENG-21579 no reloadData refetch — the Pusher "invalidate" nudge is the only refresh trigger.
const renderPatientIdentifierSrText = (item: string) => {
  let identifier = item;
  if (item) {
    identifier = "NHS Number : " + identifier.replace(/\s+/g, "").split("").join(" ");
  }
  return identifier;
};
</script>

<template>
  <div
    v-if="!patientId || !error"
    :class="{
      'm-patient-banner': true,
      'encounter-patient': true,
      secondary: props.secondary,
      light: lightBackground,
      blueAccent: blueAccent,
      'no-border': noBorder,
      small,
      banner,
      'medicus-box-shadow': boxShadow,
      loading: loading,
    }"
    :inert="loading"
  >
    <div v-if="loading === false" class="encounter-patient-info">
      <m-expander
        v-model:expanded="showInfo"
        auto
        borderless
        dense
        transition-name="expand-medicus-fast"
      >
        <template #head="{ ariaButton }">
          <m-layout-stack class="first-row" horizontal>
            <m-layout-stack full no-gap>
              <div class="encounter-patient-info-top">
                <div class="left flex">
                  <m-button
                    v-if="props.secondary !== true && patient?.extras?.length"
                    class="more-info m-mr-sm"
                    v-bind="ariaButton"
                    :icon="showInfo ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
                    :label="`${
                      ariaButton['aria-expanded'] ? 'Collapse' : 'Expand'
                    } patient information`"
                    icon-only
                    ghost
                    data-testid="button-banner-expander"
                    @click="void (showInfo = !showInfo)"
                  />
                  <!-- ENG-15152, tag needs to change dep on where it is loaded -->
                  <component
                    :is="headingLevel ? headingLevel : !secondary ? 'h2' : 'h3'"
                    class="patient-name h1-visually"
                    data-testid="display-name"
                  >
                    {{ patient ? patient.displayName : "Patient unknown" }}
                  </component>
                </div>
                <div class="right">
                  <span v-if="patient?.dateOfBirth" data-testid="date-of-birth">
                    {{ formatDate(patient?.dateOfBirth) }} ({{
                      patient?.age || `${getAge(patient?.dateOfBirth)} years old`
                    }})</span
                  ><span v-else>Date of birth unknown</span>
                  <span
                    v-if="
                      `guernsey` === tenant.getJurisdiction &&
                      patient?.guernseySocialInsuranceNumber
                    "
                    data-testid="guernsey-number"
                  >
                    {{ patient?.guernseySocialInsuranceNumber }}
                  </span>
                  <span
                    v-if="
                      (tenant.isNHS || tenant.getJurisdiction === `guernsey`) && patient?.nhsNumber
                    "
                    data-testid="nhs-number"
                    aria-hidden="true"
                  >
                    <picture>
                      <source media="(min-width:1024px)" srcset="/images/nhs/nhs-original.webp" />
                      <source media="(min-width:1024px)" srcset="/images/nhs/nhs-original.jpg" />
                      <source media="(min-width:800px)" srcset="/images/nhs/nhs-high.webp" />
                      <source media="(min-width:800px)" srcset="/images/nhs/nhs-high.jpg" />
                      <source srcset="/images/nhs/nhs-low.webp" />
                      <source srcset="/images/nhs/nhs-low.jpg" />
                      <img src="/images/nhs/nhs-low.jpg" style="width: 30px" alt="NHS number" />
                    </picture>
                    {{ patient?.nhsNumber }}
                  </span>
                  <span v-else-if="tenant.isNHS">NHS no unknown</span>
                  <!-- WCAG, best avoid aria-label on spans as it is not read out properly by screen readers.                  -->
                  <span
                    v-if="
                      (tenant.isNHS || tenant.getJurisdiction === `guernsey`) && patient?.nhsNumber
                    "
                    data-testid="nhs-number-screen-reader"
                    class="sr-only"
                    >{{ renderPatientIdentifierSrText(patient?.nhsNumber) }}
                  </span>
                </div>
              </div>

              <m-layout-stack horizontal no-gap>
                <div
                  v-if="!small && props.secondary !== true"
                  class="encounter-patient-info-details"
                >
                  <span v-if="patient?.preferredName" data-testid="preferred-name">
                    Preferred name: {{ patient?.preferredName }}</span
                  >
                  <span v-if="patient?.preferredPronouns" data-testid="preferred-pronouns">
                    Pronouns: {{ patient?.preferredPronouns }}</span
                  >
                </div>
              </m-layout-stack>
              <slot />
            </m-layout-stack>
          </m-layout-stack>
        </template>
        <m-layout-stack v-if="props.secondary !== true" class="expander-content">
          <m-separator class="separator top" />
          <m-description-list v-if="extras?.length" class="patient-info" :columns="2">
            <m-description-list-item
              v-for="({ label, value, type, link }, i) in extras"
              :key="`patient-info-${label}-${i}`"
              :label="label"
              :value="type === 'contacts' ? undefined : value"
              :data-testid="`patient-info-${i}`"
            >
              <template v-if="type === 'phone' && hasValue(value)" #text>
                <m-telephone-number :number="value" />
              </template>
              <template v-else-if="type === 'address' && hasValue(value)" #text>
                <address>
                  <MLink :href="link" target="_blank" title="Open address in Google Maps">
                    {{ value }}
                  </MLink>
                </address>
              </template>
              <template v-else-if="type === 'contacts' && value.length" #text>
                <ul class="banner-contact-list">
                  <li
                    v-for="{ id, name } in value as unknown as { id: string; name: string }[]"
                    :key="id"
                    class="banner-contact-list-item"
                  >
                    <MLink
                      :to-slideover="`/patient/data/patient-contact/view-patient-contact/${id}`"
                      inline
                      target="_blank"
                      :title="`Open contact details for ${name}`"
                    >
                      {{ name }}
                    </MLink>
                  </li>
                </ul>
              </template>
              <template v-else-if="type === 'contacts'" #text>
                <span>None recorded</span>
              </template>
            </m-description-list-item>
          </m-description-list>
        </m-layout-stack>
        <template #footer>
          <div
            v-if="
              (props.badges?.length ?? 0) > 0 ||
              (patient?.badges?.length ?? 0) > 0 ||
              patient?.testPatient ||
              patient?.namedGP ||
              patient?.registeredGPPractice
            "
            class="footer"
          >
            <m-separator class="separator footer" />
            <m-layout-stack horizontal justify-between center>
              <m-layout-stack class="badges" horizontal gap="1" wrap>
                <m-badge
                  v-for="(badge, index) in badges"
                  :key="index"
                  v-bind="badge"
                  style="--focus-ring: var(--focus-ring-light)"
                />
              </m-layout-stack>

              <m-layout-stack
                v-if="patient?.namedGP || patient?.registeredGPPractice"
                horizontal
                end
              >
                <span v-if="patient?.registeredGPPractice" data-testid="registered-gp-practice">
                  Registered GP practice: {{ patient?.registeredGPPractice }}
                </span>
                <span v-if="patient?.namedGP">Named GP: {{ patient?.namedGP }}</span>
              </m-layout-stack>
            </m-layout-stack>
          </div>
        </template>
      </m-expander>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.patient-info {
  padding: 0 var(--space-2-5);

  :deep(.description-list-item) {
    padding: 0;
    gap: 0;
    padding-bottom: var(--space-3);
  }

  :deep(.description-list-item--label) {
    font-weight: bold;
    color: var(--text-color);
  }
}

// ENG-21979 gave slotted lists in a description-list item a 16px indent; this one renders
// inline, so it opts out — `dd ul` is needed to out-specify that rule's `> dd ul`.
dd ul.banner-contact-list {
  padding-inline-start: 0;
}

.banner-contact-list {
  display: inline;
  list-style-type: none;
  padding: 0;
  // override description-list-item--value's white-space: pre-wrap — it widens the
  // ", " separator's rendered space here, adding an extra ~4px gap between names
  white-space: normal;
}
.banner-contact-list-item {
  display: inline-block;
  &:not(:last-child):after {
    padding-right: var(--space-1-5);
    content: ", ";
    display: inline;
  }
}

:where(.encounter-patient) {
  width: 100%;
  display: flex;
  box-sizing: border-box;
  align-items: flex-start;
  height: auto;
  background: var(--grey-lightest);
  min-height: 33px;
  flex: 0 0 auto;

  &.no-border {
    border-style: none;
  }

  &.small {
    padding-bottom: 0;
  }

  &.banner {
    padding: var(--space-2) var(--space-4);
    background: var(--surface-default);
    border-radius: var(--radius-default);
    border: 1px solid var(--grey-light);

    .encounter-patient-info {
      padding: 0 !important;
    }
  }
}

.badges {
  padding: var(--space-1-5) 0 var(--space-1-5) var(--space-6);
}

.right {
  margin-top: var(--space-0-5);

  > *:not(:last-child) + *::before {
    content: "•";
    margin: 0 var(--space-1-5);
  }
}

.second-row {
  padding: 0 var(--space-2-5) var(--space-1-5);

  .separator {
    margin-bottom: var(--space-1-5);
  }
}

.secondary {
  .patient-name {
    font-size: var(--font-size-xl);
  }

  .badges {
    padding: var(--space-1) 0 var(--space-0-5);
  }
}
.expander-content {
  padding: 0 0 var(--space-2-5) var(--space-4);
}

.encounter-patient.light {
  background-color: var(--surface-default);
}

.encounter-patient.blueAccent {
  background: var(--theme-accent-blue);

  color: var(--text-on-dark);

  .patient-name {
    color: var(--text-on-dark);
  }
}

.encounter-patient-avatar {
  flex: 0 0 auto;
  width: auto;
  height: auto;
  margin-right: var(--space-6);
}

.encounter-patient-no-avatar {
  height: 50px;
  margin-right: var(--space-6);
}

.encounter-patient-info {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.encounter-patient-info .encounter-patient-info-top {
  display: flex;
  align-items: center;
  justify-content: space-between;

  i {
    font-size: 1.2em !important;
  }
}

.encounter-patient-info-top > .patient-name {
  color: var(--text-color);

  font-family: Georgia;
  font-style: normal;
  font-weight: normal;
  font-size: var(--font-size-h1);

  letter-spacing: 0.01em;
  line-height: 150%;
  /* identical to box height */

  display: flex;
  align-items: center;

  text-decoration: none;
}

a.patient-name:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

:not(.secondary) {
  .encounter-patient-info-details:not(:empty) {
    padding-left: var(--space-6);
    margin-bottom: var(--space-1);
  }
}
.encounter-patient-info-details {
  display: flex;
  flex-direction: row;

  > span::after {
    content: "•";
    margin: 0 var(--space-1-5);
  }

  span:last-of-type::after {
    content: "";
  }

  > span {
    height: 21px;
  }
}

picture {
  vertical-align: middle;
}

/* apply padding to all children */
.encounter-patient-info-details > div {
  padding: var(--space-1) 0;
  padding-right: var(--space-10);
}

/* remove padding from popup-container */
.encounter-patient-info-details > .popup-container {
  cursor: pointer;
  padding-right: var(--space-1);

  align-self: flex-end;
}

/* custom style popup */
.encounter-patient-info-details > .popup-container > .popup {
  width: 176px;

  padding: 0 var(--space-2);
  margin-left: var(--space-2);

  /* center */
  top: -180%;
}

.encounter-patient-info-details > .popup-container > .popup > div {
  padding: var(--space-2) var(--space-2);
}

.patient-action-button-group {
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 auto;
}

.patient-action-button-group > button {
  margin: var(--space-2) var(--space-1);

  width: 180px;
}

.popup-container {
  position: relative;
}

.link {
  position: relative;

  font-style: normal;
  font-weight: 600;
  font-size: var(--font-size-md);
  line-height: 150%;
  /* identical to box height */

  text-decoration-line: underline;
  text-underline-offset: 3px;

  color: var(--blue-500);
}

.link:hover + .popup {
  display: block;
}

.popup {
  display: none;
  position: absolute;
  background: var(--grey-950);
  box-shadow:
    0px 4px 6px var(--shadow-colour-ambient),
    0px 2px 4px var(--shadow-colour-subtle);

  z-index: 9001;
  border-radius: var(--radius-xl);
}

.popup-center-right {
  top: -100%;
  left: 100%;
}

.popup-center-right::before {
  content: " ";
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;

  border-right: 8px solid var(--grey-950);

  position: absolute;
  top: 50%;
  bottom: 50%;
  left: -8px;
}

.popup .text,
.popup pre {
  font-style: normal;
  font-weight: normal;
  font-size: var(--font-size-md);
  line-height: 150%;

  color: var(--text-on-dark) !important;
}
</style>

<style lang="scss">
.patient-info {
  // WCAG wants at least 2px focus outline
  .medicus-outline:focus-visible::before {
    box-shadow: 0 0 0 3px var(--focus-ring-light);
  }
}

.more-info {
  border: none;
  background-color: transparent;
  padding: 0;

  button:focus {
    &::before {
      display: none !important;
    }
  }

  button:focus-visible {
    outline: 2px solid var(--focus-ring-light) !important;
  }

  > .m-icon {
    cursor: pointer;
    padding-right: var(--space-1);

    color: var(--status-blue);
  }
}

.encounter-patient.blueAccent {
  .more-info {
    span,
    .medicus-button,
    > .m-icon {
      color: var(--text-on-dark) !important;
    }
  }

  .patient-info {
    .description-list-item--value,
    .description-list-item--label {
      color: var(--text-on-dark);
    }
  }
  .m-link {
    color: var(--grey-lightest) !important;
  }
}

.encounter-patient-info {
  i {
    font-size: var(--font-size-2xl) !important;
  }
}

.m-expander > * {
  padding-left: 0;
  padding-right: 0;
}

.first-row {
  &:has(.encounter-patient-info-details:empty) {
    margin-bottom: var(--space-1-5);
  }
}

.m-expander.expanded {
  padding-bottom: 0;
}

.separator {
  background: var(--grey-light);
}
</style>
